import type { BraceletPatternDto, PatternRow } from '@miyuki/types';

import { ValidationError } from '../errors';

import { MAX_ROWS, MIN_ROWS } from './pattern.constants';
import { assertRowIndex, emptyRow, type PatternEdit } from './pattern.entity';

/**
 * Las operaciones de fila no llevan un parche de celdas sino un snapshot: al
 * cambiar la cantidad de filas, las posiciones posteriores se desplazan y un
 * parche por coordenadas dejaría de señalar lo que señalaba. El snapshot
 * comparte en memoria todas las filas que no cambiaron, así que es barato.
 */
function replaceRows(
  pattern: BraceletPatternDto,
  rows: readonly PatternRow[],
  progressDelta: number,
): PatternEdit {
  // El avance cuenta filas tejidas desde arriba, así que agregar o quitar una
  // fila por encima de donde va la usuaria corre ese número (RF-60 a RF-63).
  // Acotar es lo que cumple RF-65 cuando el diseño queda más corto que él.
  const progress = Math.min(Math.max(pattern.progress + progressDelta, 0), rows.length);

  return {
    pattern: { ...pattern, rows, progress },
    undo: { kind: 'snapshot', pattern, progressDelta: -progressDelta },
  };
}

function assertRoomForOneMore(pattern: BraceletPatternDto): void {
  if (pattern.rows.length >= MAX_ROWS) {
    throw new ValidationError(`El diseño no puede superar las ${MAX_ROWS} filas`, [
      { path: 'rows', message: `Ya tiene ${pattern.rows.length}` },
    ]);
  }
}

/** Agrega una fila vacía al final: el diseño crece hacia abajo. */
export function appendRow(pattern: BraceletPatternDto): PatternEdit {
  assertRoomForOneMore(pattern);
  // Crece por debajo de todo: no puede mover el avance (RF-64).
  return replaceRows(pattern, [...pattern.rows, emptyRow(pattern.columns)], 0);
}

/** Inserta una fila vacía en la posición indicada, empujando el resto hacia abajo. */
export function insertRowAbove(pattern: BraceletPatternDto, row: number): PatternEdit {
  assertRowIndex(pattern, row);
  assertRoomForOneMore(pattern);

  // La fila nueva entra **en** `row`, así que empuja hacia abajo también a la
  // fila actual cuando se inserta justo ahí: por eso el límite es `≤`.
  return replaceRows(
    pattern,
    [...pattern.rows.slice(0, row), emptyRow(pattern.columns), ...pattern.rows.slice(row)],
    row <= pattern.progress ? 1 : 0,
  );
}

/** Copia la fila indicada justo debajo de sí misma. */
export function duplicateRow(pattern: BraceletPatternDto, row: number): PatternEdit {
  assertRowIndex(pattern, row);
  assertRoomForOneMore(pattern);

  const original = pattern.rows[row] ?? emptyRow(pattern.columns);

  // La copia entra en `row + 1`, una posición más abajo que la inserción: solo
  // corre el avance si la original ya estaba tejida.
  return replaceRows(
    pattern,
    [...pattern.rows.slice(0, row + 1), [...original], ...pattern.rows.slice(row + 1)],
    row < pattern.progress ? 1 : 0,
  );
}

/** Elimina la fila indicada. Las posteriores suben y se renumeran solas. */
export function removeRow(pattern: BraceletPatternDto, row: number): PatternEdit {
  assertRowIndex(pattern, row);

  if (pattern.rows.length <= MIN_ROWS) {
    throw new ValidationError('El diseño debe conservar al menos una fila', [
      { path: 'rows', message: `Solo queda ${pattern.rows.length}` },
    ]);
  }

  // Si `row === progress` se elimina la fila actual misma: las tejidas siguen
  // siendo las mismas y la que ocupa su lugar pasa a ser la actual (RF-63).
  return replaceRows(
    pattern,
    [...pattern.rows.slice(0, row), ...pattern.rows.slice(row + 1)],
    row < pattern.progress ? -1 : 0,
  );
}
